import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTimes,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";
import { getAllEvents } from "../../../services/eventsServices";
import { formatDateFR, resolveMediaUrl, truncateText } from "./homeUtils";

const EVENTS_PER_PAGE = 3;
const FALLBACK_EVENT_IMAGE = "/img/placeholder-event.jpg";

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(String(value));

const LatestEvents = () => {
  const [eventsList, setEventsList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const latestEvents = await getAllEvents();

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const activeUpcomingEvents = Array.isArray(latestEvents)
          ? latestEvents
              .filter((event) => {
                const eventDate = new Date(event.date_event);
                return event.is_active && eventDate >= now;
              })
              .sort((a, b) => new Date(a.date_event) - new Date(b.date_event))
          : [];

        setEventsList(activeUpcomingEvents);
        setCurrentPage(1);
      } catch (error) {
        console.error("Failed to fetch events", error);
      }
    };

    fetchEvents();
  }, []);

  const totalPages = Math.ceil(eventsList.length / EVENTS_PER_PAGE);

  const currentEvents = useMemo(() => {
    const start = (currentPage - 1) * EVENTS_PER_PAGE;
    return eventsList.slice(start, start + EVENTS_PER_PAGE);
  }, [eventsList, currentPage]);

  return (
    <section className="bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Agenda"
          title="Les prochains "
          highlight="événements"
          subtitle="Stages, rencontres, portes ouvertes, animations : ne manquez rien de la vie associative."
        />

        {currentEvents.length === 0 ? (
          <EmptyMessage text="Aucun événement à venir pour le moment." />
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {currentEvents.map((event, index) => {
              const imageUrl = resolveMediaUrl(
                event.photoURL,
                FALLBACK_EVENT_IMAGE
              );

              return (
                <Reveal key={event.id} delay={index * 0.04}>
                  <article className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(event)}
                      className="relative block h-56 w-full overflow-hidden text-left"
                    >
                      <img
                        src={imageUrl}
                        alt={event.nom_event}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_EVENT_IMAGE;
                        }}
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/10 to-transparent" />

                      <div className="absolute left-4 top-4 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-primary shadow">
                        {formatDateFR(event.date_event)}
                      </div>
                    </button>

                    <div className="flex flex-1 flex-col p-5">
                      {event.section?.nom_section && (
                        <span className="w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                          {event.section.nom_section}
                        </span>
                      )}

                      <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                        {event.nom_event}
                      </h3>

                      <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                        <p className="flex items-center gap-2">
                          <FaCalendarAlt className="text-primary" />
                          {formatDateFR(event.date_event)}
                        </p>

                        {event.lieu_event && (
                          <p className="flex items-center gap-2">
                            <FaMapMarkerAlt className="text-primary" />
                            {event.lieu_event}
                          </p>
                        )}
                      </div>

                      {event.description_event && (
                        <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                          {truncateText(stripHtml(event.description_event), 150)}
                        </p>
                      )}

                      <div className="mt-auto pt-5">
                        <button
                          type="button"
                          onClick={() => setSelectedEvent(event)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
                        >
                          Voir le détail
                          <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}

        {eventsList.length > EVENTS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      </div>

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </section>
  );
};

function EventModal({ event, onClose }) {
  const imageUrl = resolveMediaUrl(event.photoURL, FALLBACK_EVENT_IMAGE);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <article
        className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-950"
        onClick={(eventClick) => eventClick.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 dark:bg-slate-900 dark:text-white"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div className="relative flex h-[58vh] min-h-[320px] max-h-[560px] items-center justify-center overflow-hidden bg-slate-950">
            <img
              src={imageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-2xl"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_EVENT_IMAGE;
              }}
            />

            <div className="absolute inset-0 bg-slate-950/35" />

            <img
              src={imageUrl}
              alt={event.nom_event || "Événement"}
              className="relative z-10 h-full w-full object-contain p-4 sm:p-6"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_EVENT_IMAGE;
              }}
            />
          </div>

          <div className="p-6 sm:p-8 lg:p-10">
            <div className="mb-5 flex flex-wrap gap-2 text-xs font-bold">
              {event.section?.nom_section && (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {event.section.nom_section}
                </span>
              )}

              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                <FaCalendarAlt />
                {formatDateFR(event.date_event)}
              </span>

              {event.lieu_event && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  <FaMapMarkerAlt />
                  {event.lieu_event}
                </span>
              )}
            </div>

            <h2 className="max-w-4xl text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl">
              {event.nom_event}
            </h2>

            <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-900">
              {event.description_event ? (
                hasHtml(event.description_event) ? (
                  <div
                    className="prose prose-slate max-w-none leading-8 dark:prose-invert prose-a:text-primary prose-strong:text-slate-950 dark:prose-strong:text-white"
                    dangerouslySetInnerHTML={{
                      __html: event.description_event,
                    }}
                  />
                ) : (
                  <p className="whitespace-pre-line text-base leading-8 text-slate-700 dark:text-slate-300">
                    {event.description_event}
                  </p>
                )
              ) : (
                <p className="text-slate-500 dark:text-slate-400">
                  Aucune description détaillée n’est disponible pour cet
                  événement.
                </p>
              )}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
              >
                <FaTimes />
                Fermer
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPrev, onNext }) {
  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={currentPage === 1}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-slate-950"
      >
        <FaArrowLeft />
        Précédent
      </button>

      <span className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
        Page {currentPage} / {totalPages}
      </span>

      <button
        type="button"
        onClick={onNext}
        disabled={currentPage === totalPages}
        className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        Suivant
        <FaArrowRight />
      </button>
    </div>
  );
}

function EmptyMessage({ text }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
      {text}
    </div>
  );
}

export default LatestEvents;